'use client';

import { use, type ComponentType, type ReactElement } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Route, Routes } from 'react-router-dom';

import AppRahmen from './AppRahmen';
import { legeAnmeldeFreigabeVor, legeLocaleConfigVor } from './hooks/useLanguage';
import { legeSiteConfigVor } from './hooks/useSiteConfig';
import { legeSeoVor, type SeoAntwort, type SeoSeite } from './hooks/useSeo';
import { legeKatalogVor, type Katalog } from './hooks/useKatalog';
import { legeAvDepotVor, type AvDepotVorlage } from './utils/avDepotAuswahl';
import { legeUebersetzungenVor } from './hooks/useTranslation';
import OeffentlicherBereich from './components/OeffentlicherBereich';
import { legeMusterportfoliosVor, type MusterAlle, type MusterEines } from './components/musterportfolios/musterportfolios';
import { SeitenRouter } from './shared/SeitenRouter';
import { BasisUrlKontext } from './utils/seitenUrl';
import { legeProfilVor, legeRanglisteVor } from './utils/serverVorlagen';
import { uebersetzungen } from './utils/uebersetzungsBuendel';
import type { LocaleConfig } from '../app/_seo/lokal';
import type { ServerSeite } from '../app/_seo/seiten';

/**
 * Eine öffentliche Seite, die auf dem Server entsteht (Next.js-Phase 2).
 *
 * Gerendert wird DIESELBE Komponente wie in der Single-Page-App — im selben
 * Rahmen (`AppRahmen`) —, nur zuerst auf dem Server: Crawler ohne JavaScript
 * lesen Text, Überschriften und Links, und im Browser übernimmt React das
 * HTML, statt die Seite neu aufzubauen. Eine zweite Fassung jeder Seite für
 * den Server wäre eine zweite Wahrheit über denselben Inhalt.
 *
 * Damit Server und Browser dasselbe rendern, liegen vor dem ersten Rendern
 * auf beiden Seiten dieselben Daten vor:
 * - die Sprachkonfiguration (kommt als Prop, ~10 KB) — samt den
 *   Freigabe-Schaltern der Länder,
 * - die Site-Konfiguration (Anmelde-Schalter, Bereiche, Wartungsmodus) — ohne
 *   sie stand „Kostenlos starten“ erst im Browser da, nie im Server-HTML,
 * - Titel und Beschreibung der SEO-Verwaltung (als Prop, klein),
 * - der Tarif-Katalog für Preisseite und „Über uns“ (als Prop) — die Preise
 *   stehen damit im Server-HTML,
 * - beim Altersvorsorgedepot-Rechner die Aufteilungen und die Spanne der
 *   Eingaben aus der Adresse (als Prop) — statt „wird geladen …“,
 * - die öffentliche Adresse der App (`BasisUrlKontext`), wo eine Seite eine
 *   absolute URL braucht,
 * - das Übersetzungs-Bündel der Sprache. Das ist mit ~170 KB (gzip) zu gross,
 *   um es in jede Seite zu schreiben. Es kommt über denselben Abruf wie in der
 *   App (`/api/i18n/translations/<sprache>`, vom Browser zwischengespeichert);
 *   bis er da ist, wartet React mit dem Übernehmen — das Server-HTML bleibt
 *   solange sichtbar.
 *
 * Ein Link auf einen anderen Pfad wird ein echter Seitenaufruf; Query und
 * Anker ändern sich ohne Neuladen (`SeitenRouter`).
 *
 * Jede Seite hat ihr eigenes Bündel (`spaeter`): Bis 25.10.2026 waren alle
 * Seiten fest importiert, und jede lud den Code aller anderen mit — die
 * Startseite auch die Community-Backtests samt Diagramm-Bibliothek (gemessen:
 * 238 KB ungenutztes JavaScript). Geladen wird das Bündel der Seite zugleich
 * mit den Übersetzungen, nicht danach.
 */

/** Was sich vorab anstossen lässt: das Laden eines Seitenbündels. */
interface Vorladbar {
  /** Startet den Abruf (einmal) — für das Laden zugleich mit den Übersetzungen. */
  vorladen(): Promise<unknown>;
}

/** Eine Seite, deren Modul erst geladen wird, wenn sie gebraucht wird. */
interface SpaeteSeite<P extends object> extends Vorladbar {
  (props: P): ReactElement;
}

/**
 * Lädt das Modul einer Seite auf Abruf — auf dem Server wie im Browser, mit
 * demselben `use()` wie die Übersetzungen. Kein `React.lazy` mit eigener
 * Suspense-Grenze: Die gäbe auf dem Server einen Platzhalter aus und striche
 * den Inhalt nach — ein Crawler ohne JavaScript läse ihn in einem versteckten
 * Block. Ohne Grenze wartet das Server-HTML, bis die Seite steht, und im
 * Browser behält React das Server-HTML, bis das Bündel da ist.
 */
function spaeter<P extends object = Record<string, never>>(
  laden: () => Promise<{ default: ComponentType<P> }>,
): SpaeteSeite<P> {
  let modul: Promise<ComponentType<P>> | null = null;
  const vorladen = (): Promise<ComponentType<P>> => {
    if (!modul) {
      const geladen = laden().then((m) => m.default);
      geladen.then(
        // Ist das Modul da, liest React es mit `use()` ohne zweites Warten
        // (der Status gehört zum Thenable-Protokoll von React).
        (komponente) => {
          Object.assign(geladen, { status: 'fulfilled', value: komponente });
        },
        // `use()` wirft einen gescheiterten Abruf an die Fehlergrenze, er wird
        // nicht verschluckt. Auf dem Server fragt die nächste Anfrage neu; im
        // Browser bleibt er stehen — jedes neue Rendern hätte sonst denselben
        // Abruf wieder gestartet (wie bei den Übersetzungen, s. `uebersetzungen`).
        (fehler: unknown) => {
          console.error('Seite nicht ladbar:', fehler);
          if (typeof window === 'undefined') modul = null;
        },
      );
      modul = geladen;
    }
    return modul;
  };
  function Seite(props: P): ReactElement {
    const Komponente = use(vorladen());
    return <Komponente {...props} />;
  }
  Seite.vorladen = vorladen;
  return Seite;
}

const StartPage = spaeter(() => import('./seiten/StartPage'));
const BacktestLandingPage = spaeter(() => import('./seiten/BacktestLandingPage'));
const MonteCarloLandingPage = spaeter(() => import('./seiten/MonteCarloLandingPage'));
const EntnahmeplanLandingPage = spaeter(() => import('./seiten/EntnahmeplanLandingPage'));
const BeraterLandingPage = spaeter(() => import('./seiten/BeraterLandingPage'));
const WatchlistLandingPage = spaeter(() => import('./seiten/WatchlistLandingPage'));
const DepotCheckLandingPage = spaeter(() => import('./seiten/DepotCheckLandingPage'));
const FireLandingPage = spaeter(() => import('./seiten/FireLandingPage'));
const AltersvorsorgedepotPage = spaeter(() => import('./seiten/AltersvorsorgedepotPage'));
const VorabpauschalePage = spaeter(() => import('./seiten/VorabpauschalePage'));
const PricingPage = spaeter(() => import('./seiten/PricingPage'));
const UeberUnsPage = spaeter(() => import('./seiten/UeberUnsPage'));
const DevelopersPage = spaeter(() => import('./seiten/DevelopersPage'));
const PublicLeaderboardPage = spaeter<{ view: 'portfolios' | 'architects' }>(
  () => import('./seiten/PublicLeaderboardPage'),
);
const InvestorProfilePage = spaeter(() => import('./seiten/InvestorProfilePage'));
const LexikonPage = spaeter(() => import('./seiten/LexikonPage'));
const LexikonKennzahlPage = spaeter(() => import('./seiten/LexikonKennzahlPage'));
const EntnahmeStudiePage = spaeter(() => import('./seiten/EntnahmeStudiePage'));
const MusterportfoliosPage = spaeter(() => import('./seiten/MusterportfoliosPage'));
const MusterportfolioPage = spaeter(() => import('./seiten/MusterportfolioPage'));
const AvRatgeberPage = spaeter(() => import('./seiten/AvRatgeberPage'));
const BacktestToolsPage = spaeter(() => import('./seiten/BacktestToolsPage'));
const PortfolioVisualizerAlternativePage = spaeter(() => import('./seiten/PortfolioVisualizerAlternativePage'));
const MethodikPage = spaeter(() => import('./seiten/MethodikPage'));
const VierProzentRegelPage = spaeter(() => import('./seiten/VierProzentRegelPage'));

const SEITEN: Record<ServerSeite, ComponentType> = {
  start: StartPage,
  backtest: BacktestLandingPage,
  monte_carlo: MonteCarloLandingPage,
  entnahmeplan: EntnahmeplanLandingPage,
  berater: BeraterLandingPage,
  watchlist: WatchlistLandingPage,
  depot_check: DepotCheckLandingPage,
  fire: FireLandingPage,
  av_depot: AltersvorsorgedepotPage,
  vorabpauschale: VorabpauschalePage,
  preise: PricingPage,
  ueber_uns: UeberUnsPage,
  entwickler: DevelopersPage,
  // Die Community-Seiten stehen in derselben Gast-Hülle wie in der App
  // (`PublicLayoutRoute` → `OeffentlicherBereich`).
  community: () => (
    <OeffentlicherBereich>
      <PublicLeaderboardPage view="portfolios" />
    </OeffentlicherBereich>
  ),
  architekten: () => (
    <OeffentlicherBereich>
      <PublicLeaderboardPage view="architects" />
    </OeffentlicherBereich>
  ),
  // Das Profil liest den Benutzernamen per `useParams` — dafür braucht es
  // eine Route mit dem Parameter, wie in `App.jsx`.
  profil: () => (
    <OeffentlicherBereich>
      <Routes>
        <Route path="/:land/:sprache/investorprofile/:username" element={<InvestorProfilePage />} />
      </Routes>
    </OeffentlicherBereich>
  ),
  // Das Kennzahlen-Lexikon — welche Kennzahl, liest die Seite aus der Adresse.
  lexikon: LexikonPage,
  lexikon_kennzahl: LexikonKennzahlPage,
  // Die Datenstudie liest ihre Zahlen aus dem Bündel; die Musterportfolios
  // bekommen sie als Prop (`musterportfolios`).
  studie_entnahme: EntnahmeStudiePage,
  musterportfolios: MusterportfoliosPage,
  musterportfolio: MusterportfolioPage,
  // Ratgeber Altersvorsorgedepot — welches Thema, liest die Seite aus der Adresse.
  av_ratgeber: AvRatgeberPage,
  backtest_tools: BacktestToolsPage,
  pv_alternative: PortfolioVisualizerAlternativePage,
  methodik: MethodikPage,
  // Der Ratgeber zur 4-Prozent-Regel (`/learn/4-percent-rule`).
  ratgeber_4_prozent: VierProzentRegelPage,
};

/**
 * Das Bündel, das eine Seite braucht. Die meisten Einträge in `SEITEN` SIND die
 * spät geladene Seite; die Community-Seiten stehen in einer Hülle — für sie
 * steht hier, welche Seite darin liegt.
 */
const IN_DER_HUELLE: Partial<Record<ServerSeite, Vorladbar>> = {
  community: PublicLeaderboardPage,
  architekten: PublicLeaderboardPage,
  profil: InvestorProfilePage,
};

function buendelDer(seite: ServerSeite): Vorladbar {
  const eintrag = SEITEN[seite] as Partial<Vorladbar>;
  if (typeof eintrag.vorladen === 'function') return eintrag as Vorladbar;
  const innen = IN_DER_HUELLE[seite];
  if (!innen) throw new Error(`Server-Seite „${seite}“ ohne Bündel — Eintrag in IN_DER_HUELLE fehlt`);
  return innen;
}

export interface OeffentlicheSeiteProps {
  seite: ServerSeite;
  /** `null`: Die Seite setzt Titel und Beschreibung selbst. */
  seoSchluessel: SeoSeite | null;
  land: string;
  sprache: string;
  /** Pfad mit Sprach-Präfix, wie in der Adresszeile (`/de/deutsch/portfolio-backtester`). */
  pfad: string;
  /** Query mit `?`, oder `''`. */
  suche: string;
  localeConfig: LocaleConfig;
  /** Antwort von `/api/public/site-config`, auf dem Server geholt. */
  siteConfig: Record<string, unknown>;
  seo: SeoAntwort | null;
  /** Nur für Seiten, die ihn im ersten Rendern brauchen (`MIT_KATALOG`). */
  katalog: Katalog | null;
  /** Öffentliche Adresse der App (`FRONTEND_BASE_URL`). */
  basisUrl: string;
  /** Erster Stand der Community-Seiten: Schlüssel (`ranglistenSchluessel`) und Daten. */
  rangliste: { schluessel: string; daten: unknown } | null;
  /** Erster Stand des Investorprofils. */
  profil: { name: string; daten: unknown } | null;
  /** Altersvorsorgedepot-Rechner: Aufteilungen und Tabellen der Adresse. */
  avDepot: AvDepotVorlage | null;
  /** Erster Stand der Musterportfolios: `alle` oder die Kennung, `daten` `null` = noch nicht gerechnet. */
  musterportfolios: { schluessel: string; daten: unknown } | null;
}

export default function OeffentlicheSeite({
  seite,
  seoSchluessel,
  land,
  sprache,
  pfad,
  suche,
  localeConfig,
  siteConfig,
  seo,
  katalog,
  basisUrl,
  rangliste,
  profil,
  avDepot,
  musterportfolios,
}: OeffentlicheSeiteProps) {
  // Das Bündel der Seite zuerst anstossen: Es kommt dann zugleich mit den
  // Übersetzungen, nicht erst, wenn die da sind.
  buendelDer(seite).vorladen();
  const texte = use(uebersetzungen(sprache));

  // Vor dem ersten Rendern der Seite, auf beiden Seiten gleich. Jede Vorlage
  // überschreibt nur ihren eigenen Eintrag und ist damit wiederholbar.
  legeLocaleConfigVor(localeConfig);
  legeAnmeldeFreigabeVor(localeConfig.countries);
  legeSiteConfigVor(siteConfig);
  legeUebersetzungenVor(sprache, texte);
  if (seoSchluessel && seo) legeSeoVor(seoSchluessel, sprache, seo);
  if (katalog) legeKatalogVor(katalog);
  if (rangliste) legeRanglisteVor(rangliste.schluessel, rangliste.daten);
  if (profil) legeProfilVor(profil.name, profil.daten);
  // Immer, auch mit `null` — sonst bliebe auf dem Server die Vorlage einer früheren Anfrage stehen.
  legeAvDepotVor(avDepot);
  if (musterportfolios) legeMusterportfoliosVor(musterportfolios.schluessel, musterportfolios.daten as MusterAlle | MusterEines | null);

  const Seite = SEITEN[seite];
  return (
    <HelmetProvider>
      <BasisUrlKontext.Provider value={basisUrl}>
      <SeitenRouter start={{ pathname: pfad, search: suche, hash: '' }}>
        <AppRahmen startOrt={{ country: land, language: sprache }}>
          <Seite />
        </AppRahmen>
      </SeitenRouter>
      </BasisUrlKontext.Provider>
    </HelmetProvider>
  );
}
